/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.querySelector(".thb-quick-search");
    this.button = document.getElementById("quick-search");
    this.close_button = this.container.querySelector(".thb-search-close");
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.container.querySelector(
      ".thb-quick-search--results",
    );
    this.fuzzySearchService = new FuzzySearchService();

    this.setupEventListeners();
  }

  escapeRegExp(string) {
    return string.replace(/[.+?^${}()|[]\]/g, "\$&");
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  setupEventListeners() {
    const form = this.container.querySelector("form.searchform");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    this.button.addEventListener("click", (event) => {
      event.preventDefault();
      document.querySelector(".header-section").classList.toggle("search-open");

      if (
        document
          .querySelector(".header-section")
          .classList.contains("search-open")
      ) {
        setTimeout(() => {
          this.input.focus({
            preventScroll: true,
          });
        }, 100);
        dispatchCustomEvent("search:open");
      }
    });

    // Close.
    this.close_button.addEventListener("click", (event) => {
      this.close();
      event.preventDefault();
    });
    document.addEventListener("keyup", (event) => {
      if (event.key === "Escape") {
        this.close();
      }
    });
  }

  categoryToggle() {
    this.category_toggle = this.container.querySelectorAll(
      ".search-categories a",
    );

    this.category_toggle.forEach((link) => {
      link.addEventListener("click", (event) => {
        [].forEach.call(this.category_toggle, function (el) {
          el.classList.remove("active");
        });
        link.classList.add("active");
        let target = link.getAttribute("href");
        this.container
          .querySelectorAll(".search-results")
          .forEach((section) => {
            section.classList.remove("active");
          });
        this.container.querySelector(target).classList.add("active");

        event.preventDefault();
      });
    });
  }

  getQuery() {
    return this.escapeRegExp(this.input.value.trim());
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove("active");
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    this.predictiveSearchResults.classList.add("loading");
    if (searchTerm.length >= 3) {
      try {
        const searchResults =
          await this.fuzzySearchService.performSearch(searchTerm);
        const matchingTags = searchResults.tags;

        const shopifyFormattedResults =
          this.formatAsShopifyResponse(searchResults);
        const resultsMarkup = this.renderShopifyTemplate(
          shopifyFormattedResults,
        );

        this.predictiveSearchResults.classList.remove("loading");
        this.renderSearchResults(resultsMarkup, matchingTags);
      } catch (error) {
        this.predictiveSearchResults.classList.remove("loading");
        console.error("Search error:", error);
      }
    } else {
      this.predictiveSearchResults.classList.remove("loading");
      this.predictiveSearchResults.classList.remove("active");
      return;
    }
  }

  formatAsShopifyResponse(searchResults) {
    return {
      resources: {
        results: {
          products: searchResults.products.map((p) => ({
            id: p.id,
            title: p.title,
            handle: p.handle,
            url: `/products/${p.handle}`,
            image: p.images && p.images[0] ? p.images[0].src : null,
            price: p.variants && p.variants[0] ? p.variants[0].price : null,
            compare_at_price:
              p.variants && p.variants[0]
                ? p.variants[0].compare_at_price
                : null,
            available:
              p.variants && p.variants[0] ? p.variants[0].available : false,
            product_type: p.product_type,
            vendor: p.vendor,
            tags: p.tags,
          })),
          pages: searchResults.pages.map((p) => ({
            id: p.id,
            title: p.title,
            handle: p.handle,
            url: `/pages/${p.handle}`,
          })),
        },
      },
    };
  }

  renderShopifyTemplate(data) {
    const { products, pages } = data.resources.results;

    let html = "<scroll-shadow>";
    html +=
      '<div class="search-results-container" data-predictive-search-results>';

    const hasResults = products.length > 0 || pages.length > 0;

    if (hasResults) {
      html += '<section class="search-results-categories">';

      if (products.length > 0) {
        let pageSize = pages.length;
        html += this.renderProducts(products, pageSize);
      }

      if (pages.length > 0) {
        html += this.renderPages(pages);
      }

      html += "</section>";
    }

    html += "</div>";
    html += "</scroll-shadow>";
    return html;
  }

  renderProducts(products, pageSize) {
    if (products.length === 0) return "";
    const searchTerm = this.getQuery();
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const setProductNumber = pageSize === 0 ? 10 : 6;
    const thbQuickSearchResults = document.querySelector("[data-products]");
    const heading = thbQuickSearchResults.dataset.products;
    const productItems = products
      .slice(0, setProductNumber)
      .map((product) => {
        const highlightedTitle = product.title.replace(
          regex,
          '<mark class="highlight">$1</mark>',
        );
        const productTitle = this.escapeHtml(product.title || "");
        const productUrl = this.escapeHtml(product.url || "");
        const productImage = this.escapeHtml(product.image || "");
        const productPrice = this.escapeHtml(product.price || "");
        const productCompareAtPrice = this.escapeHtml(
          product.compare_at_price || "",
        );

        return `
        <li>
          <a href="${productUrl}" title="${productTitle}" class="product-cart-item">
            ${
              productImage
                ? `
              <div class="product-cart-item-image">
                <img src="${productImage}" 
                     class="lazyautosizes lazyloaded" 
                     loading="lazy" 
                     width="1400" 
                     height="1050" 
                     alt="${productTitle}">
              </div>
            `
                : ""
            }
            <div>
              <span class="cart-product-title">
                ${highlightedTitle}
              </span>
              <span class="price">
                ${
                  productPrice
                    ? `
                  <ins>
                    <span class="amount">${this.formatPrice(productPrice)}</span>
                  </ins>
                `
                    : ""
                }
                ${
                  productCompareAtPrice && productCompareAtPrice > productPrice
                    ? `
                  <s class="price-item price-item--regular">${this.formatPrice(productCompareAtPrice)}</s>
                `
                    : ""
                }
              </span>
            </div>
          </a>
        </li>
      `;
      })
      .join("");

    return `
      <section aria-labelledby="search-products-heading">
        <h3 id="search-products-heading" class="h5 search-results-heading">${heading}</h3>
        <ul class="search-results active">
          ${productItems}
        </ul>
      </section>
    `;
  }

  renderPages(pages) {
    if (pages.length === 0) return "";

    const searchTerm = this.getQuery();
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const thbQuickSearchResults = document.querySelector("[data-pages]");
    const heading = thbQuickSearchResults.dataset.pages;
    const pageItems = pages
      .map((page) => {
        const highlightedTitle = page.title.replace(
          regex,
          '<mark class="highlight">$1</mark>',
        );
        const pageUrl = this.escapeHtml(page.url || "");

        return `
        <li>
          <a href="${pageUrl}" class="product-cart-item"><span>${highlightedTitle}</span></a>
        </li>
      `;
      })
      .join("");

    return `
      <section aria-labelledby="search-pages-heading">
        <h3 id="search-pages-heading" class="h5 search-results-heading">${heading}</h3>
        <ul class="search-results active" id="predictive-results-pages">
          ${pageItems}
        </ul>
      </section>
    `;
  }

  formatPrice(price) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  }

  addTagsToResults(tags) {
    if (tags.length === 0) return "";

    const searchTerm = this.getQuery();
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const thbQuickSearchResults = document.querySelector("[data-suggestions]");
    const heading = thbQuickSearchResults.dataset.suggestions;
    const tagItems = tags
      .map((tag) => {
        const tagHandle = tag.toLowerCase().replace(/\s+/g, "-");
        const highlightedTag = tag.replace(
          regex,
          '<mark class="highlight">$1</mark>',
        );

        return `
        <li>
          <a href="/collections/all/${tagHandle}">
            ${highlightedTag}
          </a>
        </li>
      `;
      })
      .join("");

    return `
      <section aria-labelledby="search-tags-heading" class="search-results-tags">
        <h3 id="search-tags-heading" class="h5 search-results-heading">${heading}</h3>
        <ul class="search-results-tags-list">
          ${tagItems}
        </ul>
      </section>
    `;
  }

  renderSearchResults(resultsMarkup, matchingTags) {
    const tagsMarkup = this.addTagsToResults(matchingTags);

    if (tagsMarkup) {
      const scrollShadowStart = "<scroll-shadow>";
      const containerStart =
        '<div class="search-results-container" data-predictive-search-results>';
      resultsMarkup = resultsMarkup.replace(
        scrollShadowStart + containerStart,
        scrollShadowStart + containerStart + tagsMarkup,
      );
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.classList.add("active");

    this.categoryToggle();
  }

  open() {
    document.querySelector(".header-section").classList.add("search-open");
  }

  close() {
    document.querySelector(".header-section").classList.remove("search-open");
  }
}
window.addEventListener("load", () => {
  if (typeof PredictiveSearch !== "undefined") {
    new PredictiveSearch();
  }
});
